import { readFileSync } from 'fs'
import { join, relative, sep } from 'path'
import { createRequire } from 'module'
import type { HtmlTagDescriptor } from 'vite'

/**
 * Import map so the playground IAP path resolves RC in the browser. HttpIapApi
 * loads RC via a *variable* dynamic import (`import(name)`) so a MISSING RC only
 * throws at call time instead of 500-ing the whole SDK chunk at serve time — but
 * that also stops Vite rewriting the specifier when RC IS present, leaking the
 * bare name to the browser. This map fixes the present case without reintroducing
 * the missing-RC crash. Null when RC is absent (overlay covers that); dev-only.
 */
export function buildRevenueCatImportMapTag(projectRoot: string): HtmlTagDescriptor | null {
  // RC's "exports" field gates `./package.json`, so resolve the package entry
  // and derive the package dir from the node_modules marker in its path.
  let entryFile: string
  try {
    entryFile = createRequire(join(projectRoot, 'package.json')).resolve('@revenuecat/purchases-js')
  } catch {
    return null
  }
  const marker = 'node_modules/@revenuecat/purchases-js'
  const normEntry = entryFile.split(sep).join('/')
  const idx = normEntry.lastIndexOf(marker)
  if (idx === -1) return null
  const pkgDir = normEntry.slice(0, idx + marker.length)
  // Prefer the real ESM entry (browser needs named exports, not the UMD build).
  let esmEntry = 'dist/Purchases.es.js'
  try {
    const pkg = JSON.parse(readFileSync(`${pkgDir}/package.json`, 'utf-8')) as {
      module?: string
      exports?: { '.'?: { import?: string } }
    }
    esmEntry = (pkg.exports?.['.']?.import ?? pkg.module ?? esmEntry).replace(/^\.\//, '')
  } catch {
    // Fall back to the known 1.x ESM entry.
  }
  const entryAbs = `${pkgDir}/${esmEntry}`
  // Root-relative URL for a flat install; Vite's /@fs/ escape hatch when hoisted.
  const relFromRoot = relative(projectRoot, entryAbs).split(sep).join('/')
  const specifierUrl = relFromRoot.startsWith('node_modules/') ? `/${relFromRoot}` : `/@fs/${entryAbs}`
  return {
    tag: 'script',
    attrs: { type: 'importmap' },
    children: JSON.stringify({ imports: { '@revenuecat/purchases-js': specifierUrl } }),
    injectTo: 'head-prepend',
  }
}
